
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import type { DateRange as RDPDateRange } from "react-day-picker";

export type DateRange = {
  from: Date;
  to: Date;
};

export type DateRangeSelectorProps = {
  onRangeChange: (range: DateRange) => void;
};

const DateRangeSelector = ({ onRangeChange }: DateRangeSelectorProps) => {
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    return {
      from: startOfMonth(today),
      to: endOfMonth(today),
    };
  });

  const [isCustomRange, setIsCustomRange] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState<RDPDateRange | undefined>(undefined);

  const setRangeAndNotify = (range: DateRange) => {
    setDateRange(range);
    onRangeChange(range);
  };

  const handleRangeSelect = (value: string) => {
    if (!value) return;
    const today = new Date();
    let newRange: DateRange;

    switch (value) {
      case "week":
        newRange = { from: startOfWeek(today, { weekStartsOn: 1 }), to: endOfWeek(today, { weekStartsOn: 1 }) };
        setIsCustomRange(false);
        break;
      case "month":
        newRange = { from: startOfMonth(today), to: endOfMonth(today) };
        setIsCustomRange(false);
        break;
      case "year":
        newRange = { from: startOfYear(today), to: endOfYear(today) };
        setIsCustomRange(false);
        break;
      case "custom":
        setIsCustomRange(true);
        setPendingRange({ from: dateRange.from, to: dateRange.to });
        setCalendarOpen(true);
        return;
      default:
        return;
    }

    setRangeAndNotify(newRange);
  };

  const handleConfirm = () => {
    if (pendingRange?.from && pendingRange?.to) {
      setRangeAndNotify({ from: pendingRange.from, to: pendingRange.to });
      setCalendarOpen(false);
    }
  };

  const handleCancel = () => {
    setPendingRange({ from: dateRange.from, to: dateRange.to });
    setCalendarOpen(false);
  };

  const displayRange = () => {
    if (isCustomRange) {
      return `${format(dateRange.from, "MMM dd")} - ${format(dateRange.to, "MMM dd, yyyy")}`;
    }
    const today = new Date();
    if (
      dateRange.from.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime() &&
      dateRange.to.getTime() === endOfWeek(today, { weekStartsOn: 1 }).getTime()
    ) return `Week of ${format(today, "MMM dd, yyyy")}`;
    if (
      dateRange.from.getTime() === startOfMonth(today).getTime() &&
      dateRange.to.getTime() === endOfMonth(today).getTime()
    ) return format(today, "MMMM yyyy");
    if (
      dateRange.from.getTime() === startOfYear(today).getTime() &&
      dateRange.to.getTime() === endOfYear(today).getTime()
    ) return format(today, "yyyy");
    return `${format(dateRange.from, "MMM dd")} - ${format(dateRange.to, "MMM dd, yyyy")}`;
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-center">
      <ToggleGroup type="single" defaultValue="month" onValueChange={handleRangeSelect}>
        <ToggleGroupItem value="week">Week</ToggleGroupItem>
        <ToggleGroupItem value="month">Month</ToggleGroupItem>
        <ToggleGroupItem value="year">Year</ToggleGroupItem>
        <ToggleGroupItem value="custom">Custom</ToggleGroupItem>
      </ToggleGroup>

      {isCustomRange && (
        <Popover open={calendarOpen} onOpenChange={(open) => {
          setCalendarOpen(open);
          if (open) setPendingRange({ from: dateRange.from, to: dateRange.to });
        }}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn("justify-start text-left font-normal w-full sm:w-auto")}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {displayRange()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={pendingRange?.from ?? dateRange.from}
              selected={pendingRange}
              onSelect={(range) => setPendingRange(range)}
              numberOfMonths={2}
              className={cn("p-3 pointer-events-auto")}
            />
            <div className="flex items-center justify-between gap-2 border-t p-3">
              <div className="text-xs text-muted-foreground">
                {pendingRange?.from ? format(pendingRange.from, "MMM dd, yyyy") : "Start date"}
                {" – "}
                {pendingRange?.to ? format(pendingRange.to, "MMM dd, yyyy") : "End date"}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={!pendingRange?.from || !pendingRange?.to}
                >
                  Select
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {!isCustomRange && (
        <div className="text-sm font-medium px-3 py-2 border rounded-md">
          {displayRange()}
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;

